import React, { createContext, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import cronstrue from 'cronstrue';

import { useAuth } from '../../authentication';
import { useCreateSpaceRefreshScheduleMutation } from '../hooks/useCreateSpaceRefreshScheduleMutation';
import { useUpdateSpaceRefreshScheduleMutation } from '../hooks/useUpdateSpaceRefreshScheduleMutation';
import { useOrganizations } from '../../organization/providers';
import { useNotifications } from '../../../providers/NotificationsProvider';

const ShedulesContext = createContext();

const SchedulesProvider = ({ children }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const createSpaceRefreshScheduleMutation = useCreateSpaceRefreshScheduleMutation(accessToken);
  const updateSpaceRefreshScheduleMutation = useUpdateSpaceRefreshScheduleMutation(accessToken);

  const createSpaceRefreshSchedule = useCallback(
    async ({ spaceId, cronSchedule, timezone, spaceName }) => {
      const queryKey = ['chat', 'spaces', currentOrganization?.id];
      const prevState = await queryClient.getQueryData(queryKey);
      if (currentOrganization?.id) {
        try {
          const newSpaceSchedule = await createSpaceRefreshScheduleMutation.mutateAsync({
            data: { cronSchedule, timezone },
            spaceId,
            organizationId: currentOrganization?.id
          });

          // Optimistically update the space with the new schedule
          queryClient.setQueryData(queryKey, (oldData) =>
            oldData.map((space) => {
              if (space.id === spaceId) {
                return {
                  ...space,
                  spaceRefreshSchedule: newSpaceSchedule.data
                };
              }
              return space;
            })
          );

          const humanizedCronSchedule = cronstrue.toString(newSpaceSchedule.data.cronSchedule, {
            verbose: true
          });

          addNotification({
            icon: 'recurring',
            type: 'info',
            message: `Space ${spaceName} will refresh ${
              humanizedCronSchedule.charAt(0).toLowerCase() + humanizedCronSchedule.slice(1)
            }`
          });
          return newSpaceSchedule.data;
        } catch (err) {
          addNotification({
            type: 'error',
            message: 'Failed to create space refresh schedule'
          });
          queryClient.setQueryData(queryKey, () => prevState);
        }
      }
      return null;
    },
    [createSpaceRefreshScheduleMutation, currentOrganization?.id, queryClient]
  );

  const updateSpaceRefreshSchedule = useCallback(
    async ({ spaceId, spaceRefreshScheduleId, spaceName, ...data }) => {
      const queryKey = ['chat', 'spaces', currentOrganization?.id];
      const prevState = await queryClient.getQueryData(queryKey);
      if (currentOrganization?.id) {
        try {
          const updatedSpaceSchedule = await updateSpaceRefreshScheduleMutation.mutateAsync({
            data,
            organizationId: currentOrganization?.id,
            spaceRefreshScheduleId
          });

          // Optimistically update the space with the new schedule
          queryClient.setQueryData(queryKey, (oldData) =>
            oldData.map((space) => {
              if (space.id === spaceId) {
                return {
                  ...space,
                  spaceRefreshSchedule: {
                    ...space.spaceRefreshSchedule,
                    ...data
                  }
                };
              }
              return space;
            })
          );

          const humanizedCronSchedule = cronstrue.toString(data.cronSchedule, {
            verbose: true
          });

          addNotification({
            icon: 'recurring',
            type: 'info',
            message: `Space ${spaceName} will refresh ${
              humanizedCronSchedule.charAt(0).toLowerCase() + humanizedCronSchedule.slice(1)
            }`
          });
          return updatedSpaceSchedule.data;
        } catch (err) {
          addNotification({
            type: 'error',
            message: 'Failed to update space refresh schedule'
          });
          queryClient.setQueryData(queryKey, () => prevState);
        }
      }
      return null;
    },
    [updateSpaceRefreshScheduleMutation, currentOrganization?.id, queryClient]
  );

  const value = useMemo(
    () => ({
      createSpaceRefreshSchedule,
      updateSpaceRefreshSchedule
    }),
    [createSpaceRefreshSchedule, updateSpaceRefreshSchedule]
  );

  return <ShedulesContext.Provider value={value}>{children}</ShedulesContext.Provider>;
};

SchedulesProvider.defaultProps = {
  children: null
};

SchedulesProvider.propTypes = {
  children: PropTypes.node
};

const useSchedules = () => useContext(ShedulesContext);

export { SchedulesProvider, ShedulesContext, useSchedules };
